import {
  menu,
  popFilterableSimpleMenu,
  type PopupTarget,
  popupTargetFromElement,
} from '@blocksuite/affine-components/context-menu';
import {
  ArrowDownSmallIcon,
  MoreHorizontalIcon,
  PlusIcon,
} from '@blocksuite/icons/lit';
import { css, html, nothing } from 'lit';
import { property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';

import { dataViewI18nT } from '../../core/i18n.js';
import { WidgetBase } from '../../core/widget/widget-base.js';
import { popViewOptions } from '../tools/presets/view-options/view-options.js';
import { viewIdExists } from '../view-existence.js';

const getLocalizedViewName = (type: string, defaultName: string): string =>
  type === 'table' ? dataViewI18nT('table.defaultName') : defaultName;

export interface CompactLeadingViewItem {
  label: string;
  isSelected: boolean;
  select: () => void;
}

export class DataViewHeaderViews extends WidgetBase {
  static override styles = css`
    data-view-header-views {
      height: 28px;
      display: flex;
      user-select: none;
      gap: 4px;
    }
    data-view-header-views::-webkit-scrollbar-thumb {
      width: 1px;
    }

    .database-view-button {
      height: 100%;
      cursor: pointer;
      padding: 2px 4px;
      border-radius: 4px;
      font-size: 14px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: var(--streamlain-text-secondary-color);
      white-space: nowrap;
      max-width: 200px;
      min-width: 28px;
      transition: opacity 0.15s ease;
    }

    .database-view-button .name {
      align-items: center;
      font-size: 15px;
      line-height: 24px;
      overflow: hidden;
      text-overflow: ellipsis;
      font-weight: 500;
      padding-right: 2px;
    }

    .database-view-button .icon {
      margin-right: 6px;
      display: block;
      flex-shrink: 0;
    }

    .database-view-button .icon svg {
      width: 16px;
      height: 16px;
    }

    .database-view-button.selected {
      color: var(--streamlain-text-primary-color);
      background-color: var(--streamlain-hover-color-filled);
    }

    .database-view-button.compact {
      border: 0;
      box-sizing: border-box;
      background: transparent;
      font: inherit;
      gap: 6px;
      max-width: 180px;
      padding: 2px 6px;
    }

    .database-view-button.compact.selected {
      background-color: var(--streamlain-hover-color-filled);
    }

    .database-view-button.compact .icon {
      margin-right: 0;
    }

    .database-view-button.compact .name {
      min-width: 0;
      padding-right: 0;
    }

    .database-view-button.compact .chevron {
      display: flex;
      flex: 0 0 auto;
      font-size: 14px;
    }

    .compact-view-switcher {
      height: 28px;
      display: inline-flex;
      overflow: hidden;
      flex: 0 1 auto;
      align-items: center;
      position: relative;
      z-index: 1;
      border: 1px solid var(--streamlain-border-color);
      border-radius: 6px;
      background: var(--streamlain-background-primary-color);
    }

    .compact-view-switcher .database-view-button.compact {
      height: 26px;
      border-radius: 0;
      position: relative;
      z-index: 2;
    }

    .compact-quick-view {
      width: 28px;
      height: 26px;
      padding: 5px;
      border: 0;
      border-left: 1px solid var(--streamlain-border-color);
      display: inline-flex;
      flex: 0 0 28px;
      align-items: center;
      justify-content: center;
      color: var(--streamlain-text-secondary-color);
      background: var(--streamlain-background-secondary-color);
      cursor: pointer;
      position: relative;
      z-index: 2;
    }

    .compact-quick-view:hover {
      color: var(--streamlain-primary-color);
      background: var(--streamlain-hover-color-filled);
    }

    .compact-quick-view uni-lit,
    .compact-quick-view uni-lit svg {
      width: 16px;
      height: 16px;
    }

    .database-view-button.dragging {
      opacity: 0.4;
    }

    .database-view-button.drag-over-left {
      box-shadow: -2px 0 0 0 var(--streamlain-primary-color);
    }

    .database-view-button.drag-over-right {
      box-shadow: 2px 0 0 0 var(--streamlain-primary-color);
    }

    button.database-view-button {
      border: 0;
      background: transparent;
      font: inherit;
    }
  `;

  private _dragSourceId: string | null = null;
  private _dragOverId: string | null = null;
  private _dragOverSide: 'left' | 'right' | null = null;

  private _onDragStart(event: DragEvent, id: string) {
    if (this.readonly) {
      event.preventDefault();
      return;
    }
    this._dragSourceId = id;
    event.dataTransfer!.effectAllowed = 'move';
    event.dataTransfer!.setData('text/plain', id);
    const target = event.currentTarget as HTMLElement;
    requestAnimationFrame(() => target.classList.add('dragging'));
  }

  private _onDragOver(event: DragEvent, id: string) {
    if (!this._dragSourceId || this._dragSourceId === id) return;
    event.preventDefault();
    event.dataTransfer!.dropEffect = 'move';

    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    const midX = rect.left + rect.width / 2;
    const side = event.clientX < midX ? 'left' : 'right';

    if (this._dragOverId !== id || this._dragOverSide !== side) {
      this._clearDragOverStyles();
      this._dragOverId = id;
      this._dragOverSide = side;
      (event.currentTarget as HTMLElement).classList.add(
        side === 'left' ? 'drag-over-left' : 'drag-over-right'
      );
    }
  }

  private _onDragLeave(event: DragEvent) {
    const el = event.currentTarget as HTMLElement;
    el.classList.remove('drag-over-left', 'drag-over-right');
    if (this._dragOverId === el.dataset.viewId) {
      this._dragOverId = null;
      this._dragOverSide = null;
    }
  }

  private _onDrop(event: DragEvent, targetId: string) {
    event.preventDefault();
    this._clearDragOverStyles();

    const sourceId = this._dragSourceId;
    if (!sourceId || sourceId === targetId) return;

    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    const midX = rect.left + rect.width / 2;
    const before = event.clientX < midX;

    this.viewManager.moveTo(sourceId, { id: targetId, before });
    this._resetDragState();
  }

  private _onDragEnd() {
    this._clearDragOverStyles();
    this._resetDragState();
    this.renderRoot
      .querySelectorAll('.dragging')
      .forEach(el => el.classList.remove('dragging'));
  }

  private _clearDragOverStyles() {
    this.renderRoot
      .querySelectorAll('.drag-over-left, .drag-over-right')
      .forEach(el => el.classList.remove('drag-over-left', 'drag-over-right'));
  }

  private _resetDragState() {
    this._dragSourceId = null;
    this._dragOverId = null;
    this._dragOverSide = null;
  }

  private get selectedViewIdOrCurrent() {
    const selectedViewId =
      this.selectedViewId === undefined
        ? this.viewManager.currentViewId$.value
        : this.selectedViewId;
    return selectedViewId &&
      !viewIdExists(this.viewManager.views$, selectedViewId)
      ? this.viewManager.currentViewId$.value
      : selectedViewId;
  }

  private addView(type: string) {
    const id = this.viewManager.viewAdd(type);
    this.viewManager.setCurrentView(id);
    this.onChangeView?.(id); // Persist view state to storage
    this.dataViewLogic.root.config.eventTrace('AddDatabaseView', {
      type: type,
    });
  }

  _addViewMenu = (event: MouseEvent) => {
    popFilterableSimpleMenu(
      popupTargetFromElement(event.currentTarget as HTMLElement),
      this.dataSource.viewMetas.map(v => {
        return menu.action({
          name: v.model.defaultName,
          prefix: html`<uni-lit .uni=${v.renderer.icon}></uni-lit>`,
          select: () => {
            this.addView(v.type);
          },
        });
      })
    );
  };

  private readonly _viewMenuItems = (viewIds: readonly string[]) =>
    viewIds.flatMap(id => {
      if (!viewIdExists(this.viewManager.views$, id)) {
        return [];
      }
      const openViewOption = (event: MouseEvent) => {
        event.stopPropagation();
        this.openViewOption(
          popupTargetFromElement(event.currentTarget as HTMLElement),
          id
        );
      };
      const view = this.viewManager.viewGet(id);
      if (!view) {
        return [];
      }
      return menu.action({
        prefix: html`<uni-lit .uni=${this.getRenderer(id)?.icon}></uni-lit>`,
        name: view.name$.value ?? '',
        label: () => html`${view.name$.value}`,
        isSelected: this.selectedViewIdOrCurrent === id,
        hide: () => !viewIdExists(this.viewManager.views$, id),
        select: () => {
          if (!viewIdExists(this.viewManager.views$, id)) {
            return;
          }
          this.viewManager.setCurrentView(id);
          this.onChangeView?.(id);
        },
        postfix: html`<button
          type="button"
          aria-label=${dataViewI18nT('view.edit')}
          title=${dataViewI18nT('view.edit')}
          class="dv-hover dv-round-4"
          @click="${openViewOption}"
          style="display:flex;align-items:center;border:0;background:transparent;padding:0;color:inherit;"
        >
          ${MoreHorizontalIcon()}
        </button>`,
      });
    });

  private readonly _showCompactOverflow = (
    event: Event,
    viewIds: readonly string[]
  ) => {
    popFilterableSimpleMenu(
      popupTargetFromElement(event.currentTarget as HTMLElement),
      [menu.group({ items: this._viewMenuItems(viewIds) })]
    );
  };

  _showMore = (event: Event) => {
    const views = this.viewManager.views$.value;
    popFilterableSimpleMenu(
      popupTargetFromElement(event.currentTarget as HTMLElement),
      [
        menu.group({
          items: [
            ...(this.compactLeadingItem
              ? [
                  menu.action({
                    name: this.compactLeadingItem.label,
                    isSelected: this.compactLeadingItem.isSelected,
                    select: this.compactLeadingItem.select,
                  }),
                ]
              : []),
            ...this._viewMenuItems(views),
          ],
        }),
        menu.group({
          items: this.dataSource.viewMetas.map(v => {
            const viewName = getLocalizedViewName(v.type, v.model.defaultName);
            return menu.action({
              name: dataViewI18nT('view.create', { name: viewName }),
              hide: () => this.readonly,
              prefix: PlusIcon(),
              select: () => {
                this.addView(v.type);
              },
            });
          }),
        }),
      ]
    );
  };

  openViewOption = (target: PopupTarget, id: string) => {
    if (this.readonly) {
      return;
    }
    if (!this.viewManager.viewGet(id)) {
      return;
    }
    if (this.viewManager.currentViewId$.value !== id) {
      this.viewManager.setCurrentView(id);
      this.onChangeView?.(id);
    }
    const selectedViewLogic = this.dataViewLogic.root.currentView$.value;
    if (selectedViewLogic?.view.id !== id) {
      return;
    }
    popViewOptions(target, selectedViewLogic);
  };

  renderMore = (count: number) => {
    const views = this.viewManager.views$.value;
    if (count === views.length) {
      if (this.readonly) {
        return;
      }
      const label = dataViewI18nT('view.add');
      return html`<button
        type="button"
        class="database-view-button dv-icon-16 dv-hover"
        data-testid="database-add-view-button"
        aria-label=${label}
        title=${label}
        @click="${this._addViewMenu}"
      >
        ${PlusIcon()}
      </button>`;
    }
    return html`
      <div class="database-view-button dv-hover" @click="${this._showMore}">
        ${views.length - count} More
      </div>
    `;
  };

  renderViews = () => {
    const views = this.viewManager.views$.value;
    const draggable = !this.readonly && views.length > 1;
    return views.map(id => () => {
      const classList = classMap({
        'database-view-button': true,
        'dv-hover': true,
        selected: this.selectedViewIdOrCurrent === id,
      });
      const view = this.viewManager.viewDataGet(id);
      return html`
        <div
          class="${classList}"
          style="margin-right: 4px;"
          data-view-id="${id}"
          draggable="${draggable}"
          @click="${(event: MouseEvent) => this.clickView(event, id)}"
          @dragstart="${(e: DragEvent) => this._onDragStart(e, id)}"
          @dragover="${(e: DragEvent) => this._onDragOver(e, id)}"
          @dragleave="${(e: DragEvent) => this._onDragLeave(e)}"
          @drop="${(e: DragEvent) => this._onDrop(e, id)}"
          @dragend="${() => this._onDragEnd()}"
        >
          <uni-lit class="icon" .uni="${this.getRenderer(id)?.icon}"></uni-lit>
          <div class="name">${view?.name}</div>
        </div>
      `;
    });
  };

  get readonly() {
    return this.viewManager.readonly$.value;
  }

  private getRenderer(viewId: string) {
    return this.dataSource.viewMetaGetById(viewId)?.renderer;
  }

  private clickView(event: MouseEvent, id: string) {
    if (this.viewManager.currentViewId$.value !== id) {
      this.viewManager.setCurrentView(id);
      this.onChangeView?.(id);
      return;
    }
    if (this.activateCurrentViewOnClick) {
      this.onChangeView?.(id);
      return;
    }
    if (!this.openOptionsOnActiveViewClick) {
      return;
    }
    this.openViewOption(
      popupTargetFromElement(event.currentTarget as HTMLElement),
      id
    );
  }

  private renderCompactView() {
    const views = this.viewManager.views$.value;
    const leadingItem = this.compactLeadingItem?.isSelected
      ? this.compactLeadingItem
      : undefined;
    const viewId =
      this.selectedViewIdOrCurrent ??
      this.viewManager.currentViewId$.value ??
      views[0];
    if (!leadingItem && !viewId) {
      return;
    }
    const view = viewId ? this.viewManager.viewDataGet(viewId) : undefined;
    const classList = classMap({
      'database-view-button': true,
      compact: true,
      selected:
        leadingItem !== undefined || this.selectedViewIdOrCurrent === viewId,
      'dv-hover': true,
    });
    const label = dataViewI18nT('view.switch');
    const compactViewIds = views.filter(
      id => leadingItem !== undefined || id !== viewId
    );
    const quickViewIds = compactViewIds.slice(0, 2);
    const overflowViewIds = compactViewIds.slice(2);
    const moreLabel = dataViewI18nT('calendar.action.more');
    return html`
      <div class="compact-view-switcher">
        <button
          type="button"
          class="${classList}"
          data-testid="database-view-switcher"
          data-view-id="${leadingItem ? '' : viewId}"
          aria-label=${label}
          title=${label}
          @click="${this._showMore}"
        >
          ${leadingItem
            ? nothing
            : html`<uni-lit
                class="icon"
                .uni="${viewId ? this.getRenderer(viewId)?.icon : undefined}"
              ></uni-lit>`}
          <div class="name">${leadingItem?.label ?? view?.name}</div>
          <span class="chevron">${ArrowDownSmallIcon()}</span>
        </button>
        ${quickViewIds.map(id => {
          const quickView = this.viewManager.viewDataGet(id);
          const quickLabel = quickView?.name ?? label;
          return html`
            <button
              type="button"
              class="compact-quick-view"
              data-testid="database-view-quick-switch"
              data-view-id=${id}
              aria-label=${quickLabel}
              title=${quickLabel}
              @click=${(event: MouseEvent) => this.clickView(event, id)}
            >
              <uni-lit .uni=${this.getRenderer(id)?.icon}></uni-lit>
            </button>
          `;
        })}
        ${overflowViewIds.length === 0
          ? nothing
          : html`<button
              type="button"
              class="compact-quick-view"
              data-testid="database-view-overflow"
              aria-label=${moreLabel}
              title=${moreLabel}
              @click=${(event: MouseEvent) =>
                this._showCompactOverflow(event, overflowViewIds)}
            >
              ${MoreHorizontalIcon()}
            </button>`}
      </div>
    `;
  }

  override render() {
    if (this.compact) {
      return this.renderCompactView();
    }
    return html`
      <component-overflow
        .renderItem="${this.renderViews()}"
        .renderMore="${this.renderMore}"
      ></component-overflow>
    `;
  }

  @property({ attribute: false })
  accessor activateCurrentViewOnClick = false;

  @property({ attribute: false })
  accessor compact = false;

  @property({ attribute: false })
  accessor compactLeadingItem: CompactLeadingViewItem | undefined;

  @property({ attribute: false })
  accessor onChangeView: ((id: string) => void) | undefined;

  @property({ attribute: false })
  accessor openOptionsOnActiveViewClick = true;

  @property({ attribute: false })
  accessor selectedViewId: string | null | undefined = undefined;
}

declare global {
  interface HTMLElementTagNameMap {
    'data-view-header-views': DataViewHeaderViews;
  }
}
